import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, CheckCircle2, ArrowRight, Heart } from "lucide-react";

export default function Suggest() {
  const [form, setForm] = useState({
    farm_name: "",
    city: "",
    description: "",
    submitter_name: "",
    submitter_email: "",
    submitter_type: "famiglia",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/suggestions", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-[#FBF9F6] px-6 py-14">
        <div className="w-full max-w-xl rounded-[2rem] bg-white border border-[#E5DCD0] p-10 md:p-14 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center bg-[#4A6B53]/10 text-[#4A6B53]">
            <CheckCircle2 size={32}/>
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-[#2C312E]">
            Grazie di cuore!
          </h1>
          <p className="mt-4 text-[#58625B] leading-relaxed">
            La tua segnalazione è stata ricevuta. Il direttivo del Network valuterà
            <span className="font-bold text-[#2C312E]"> {form.farm_name}</span> di {form.city}
            {" "}e si metterà in contatto con la realtà per proporre l'ammissione al Network.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/fattorie" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-3 text-sm font-bold hover:bg-[#3D5844] transition-all">
              Esplora le fattorie del Network <ArrowRight size={16}/>
            </Link>
            <button
              onClick={() => { setDone(false); setForm({ farm_name: "", city: "", description: "", submitter_name: "", submitter_email: "", submitter_type: "famiglia" }); }}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E5DCD0] px-6 py-3 text-sm font-bold hover:border-[#C8795A] hover:text-[#C8795A]"
            >
              Segnala un'altra realtà
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF9F6] min-h-[calc(100vh-80px)]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/8033891/pexels-photo-8033891.jpeg?auto=compress&cs=tinysrgb&w=2000" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-br from-[#C8795A]/85 to-[#2C312E]/65"/>
        </div>
        <div className="relative mx-auto max-w-4xl px-6 md:px-10 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white mb-5">
            <Sparkles size={14}/> Segnala una realtà
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl text-white leading-tight">
            Hai vissuto una bella<br/>esperienza in fattoria?
          </h1>
          <p className="mt-5 text-white/90 max-w-2xl text-lg">
            Aiutaci a far crescere il Network: segnala una realtà educativa in natura
            non ancora iscritta. Valuteremo la proposta e contatteremo l'azienda.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 md:px-10 py-14 md:py-20">
        <form onSubmit={submit} className="rounded-[2rem] bg-white border border-[#E5DCD0] p-6 md:p-10 shadow-sm">
          <h2 className="font-display font-black text-2xl md:text-3xl text-[#2C312E]">
            La realtà che vuoi segnalare
          </h2>
          <p className="mt-2 text-[#58625B] mb-8">
            Bastano tre informazioni essenziali — al resto pensiamo noi.
          </p>

          <div className="space-y-5">
            <Field
              label="Nome della realtà / fattoria"
              required
              value={form.farm_name}
              onChange={(v) => upd("farm_name", v)}
              testid="farm-name"
              placeholder="Es. Fattoria del Sole"
            />
            <Field
              label="Comune"
              required
              value={form.city}
              onChange={(v) => upd("city", v)}
              testid="suggestion-city"
              placeholder="Es. Macerata"
            />
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">
                Breve descrizione dell'esperienza *
              </span>
              <textarea
                required
                rows={5}
                value={form.description}
                onChange={(e) => upd("description", e.target.value)}
                placeholder="Raccontaci com'è andata: che attività hai vissuto, perché te ne sei innamorato/a, perché pensi che meriti di entrare nel Network…"
                className="mt-2 w-full rounded-2xl border-2 border-[#E5DCD0] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent transition-all"
                data-testid="suggestion-description"
              />
              <span className="text-xs text-[#7A7369] mt-1 block">{form.description.length}/600 caratteri consigliati</span>
            </label>
          </div>

          {/* Optional: chi segnala */}
          <div className="mt-10">
            <h3 className="font-display font-extrabold text-lg text-[#2C312E] mb-1">Chi sei? (facoltativo)</h3>
            <p className="text-sm text-[#58625B] mb-5">
              Se vuoi essere ricontattato, lasciaci nome ed email. Altrimenti la segnalazione resta anonima.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2 grid grid-cols-2 gap-2">
                {[
                  { k: "famiglia", l: "Sono una famiglia" },
                  { k: "scuola", l: "Rappresento una scuola" },
                ].map((o) => (
                  <button type="button" key={o.k} onClick={() => upd("submitter_type", o.k)}
                    className={`rounded-2xl border-2 p-4 text-sm font-bold transition-all ${form.submitter_type === o.k ? "border-[#4A6B53] bg-[#4A6B53]/5 text-[#2C312E]" : "border-[#E5DCD0] text-[#58625B]"}`}
                    data-testid={`submitter-type-${o.k}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              <Field label="Nome" value={form.submitter_name} onChange={(v) => upd("submitter_name", v)} testid="submitter-name"/>
              <Field label="Email" type="email" value={form.submitter_email} onChange={(v) => upd("submitter_email", v)} testid="submitter-email"/>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8795A] text-white py-4 font-bold hover:bg-[#B3684B] disabled:opacity-60 transition-all shadow-sm"
            data-testid="suggestion-submit"
          >
            {submitting ? <Loader2 size={18} className="animate-spin"/> : <Send size={16}/>}
            Invia segnalazione
          </button>

          <p className="mt-4 text-xs text-[#7A7369] text-center flex items-center justify-center gap-1.5">
            <Heart size={12} className="text-[#C8795A]"/>
            Le segnalazioni vengono inoltrate a info@agrigiochiamo.it e valutate dal direttivo del Network.
          </p>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent transition-all"
        data-testid={testid}
      />
    </label>
  );
}
